import React from 'react';
import { MapPin, AlertTriangle } from 'lucide-react';
import type { SiteEntity } from '../../types/site';
import { materializeSiteBoundaries, computeAggregate, siteHasUsableLocation } from '../../lib/siteBoundaryGeometry';
import { HeroCoverBoundaryOverlay } from './report/covers/HeroCoverBoundaryOverlay';
import { useResolvedTheme } from '../../lib/useAuroraMapId';
import { resolveSiteLayerOverlay } from '../../lib/reportBranding';
import { AURORA_PARCEL_COLORS as PARCEL_COLORS } from '../../config/siteLayerColors';
import { SiteBoundaryParcelLegend } from './report/SiteBoundaryParcelLegend';

const PARCEL_STROKE_COLORS = PARCEL_COLORS.map((c) => c.dot);

export function SiteSidebarActiveBoundary({ site, isActive = true }: { site: SiteEntity; isActive?: boolean }) {
  // CSS-collapsed portfolio cards must not run the detailed polygon label solver.
  // Keep the guard outside the mounted component so its hooks remain unconditional.
  return isActive ? <MountedSiteSidebarActiveBoundary site={site} /> : null;
}

function MountedSiteSidebarActiveBoundary({ site }: { site: SiteEntity }) {
  const onDark = useResolvedTheme() === 'dark';
  
  const parcels = React.useMemo(() => {
    return materializeSiteBoundaries({
      siteBoundaries: site.siteBoundaries,
      siteBoundary: site.siteBoundary,
      siteArea: site.siteArea,
      sitePerimeter: site.sitePerimeter,
      siteCentroid: site.siteCentroid,
    });
  }, [site.siteBoundaries, site.siteBoundary, site.siteArea, site.sitePerimeter, site.siteCentroid]);

  const aggregate = React.useMemo(() => {
    if (parcels.length === 0) return null;
    if (site.siteBoundaryAggregate) return site.siteBoundaryAggregate;
    return computeAggregate(parcels);
  }, [parcels, site.siteBoundaryAggregate]);

  const hasParcels = parcels.length > 0;
  const hasUsableLocation = siteHasUsableLocation(site);

  const { kmzLayers, layerIndicators } = React.useMemo(() => {
    const all = resolveSiteLayerOverlay(site);
    const polygonsOnly = all.kmzLayers.filter((layer) => layer.geometry.type === 'polygon');
    const polygonIds = new Set(polygonsOnly.map((layer) => layer.id));
    return {
      kmzLayers: polygonsOnly,
      layerIndicators: all.layerIndicators.filter((indicator) => polygonIds.has(indicator.id)),
    };
  }, [site]);

  const hasDetailedOverlay = (hasParcels && !!aggregate) || kmzLayers.length > 0;

  return (
    <div className="space-y-2 mt-3 pt-3 border-t border-border" data-testid="sidebar-active-boundary">
      <div className="flex items-center gap-1.5 type-body font-bold uppercase tracking-widest text-text-secondary">
        <MapPin className="w-3 h-3" /> Site boundary
      </div>
      
      {hasDetailedOverlay ? (
        <div
          className="border border-border rounded-lg overflow-hidden aspect-[4/3] px-2 py-3 bg-inset"
        >
          <HeroCoverBoundaryOverlay
            report={{
              siteBoundary: site.siteBoundary,
              siteBoundaries: site.siteBoundaries,
              siteBoundaryAggregate: site.siteBoundaryAggregate,
              siteArea: site.siteArea,
              sitePerimeter: site.sitePerimeter,
              siteCentroid: site.siteCentroid,
            }}
            layers={kmzLayers}
            layerLabels={layerIndicators}
            textTone={onDark ? 'on-dark' : 'on-light'}
            parcelStrokeColors={PARCEL_STROKE_COLORS}
            strokeScale={0.6}
            className="h-full w-full max-w-none aspect-auto opacity-100"
          />
        </div>
      ) : !hasUsableLocation ? (
        <div className="border border-warning/40 rounded-lg bg-warning/5 overflow-hidden">
          <div className="min-h-[100px] flex flex-col items-center justify-center gap-1 text-center px-3 py-3">
            <AlertTriangle className="w-4 h-4 text-warning" aria-hidden />
            <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
              No location yet
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-lg bg-state-hover overflow-hidden">
          <div className="h-[100px] flex items-center justify-center type-body text-text-muted uppercase tracking-widest text-center px-3">
            No boundary drawn
          </div>
        </div>
      )}

      {/* J7 — the per-parcel ledger is now the shared SiteBoundaryParcelLegend
          (also used on the report Site-Overview map). Single-parcel sites show
          the one-line "N acres" caption; multi-parcel sites show the
          TOTAL / dedup / per-parcel-rows ledger. */}
      {hasParcels && aggregate && (
        <SiteBoundaryParcelLegend
          parcels={parcels}
          aggregate={aggregate}
          singleParcelAcres={site.siteArea}
        />
      )}
    </div>
  );
}
